import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useFormContext, useWatch } from 'react-hook-form'
import { PencilLine, Plus, Trash2, Play, Pause } from 'lucide-react'
import { Modal, ModalContent, ModalHeader, ModalBody, ModalFooter } from '@/components/ui/Modal'
import { Button } from '@/components/base/buttons'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { playGreetingAudio } from '@/hooks/api/useGreetings'
import { getLanguageFlag, getLanguageDisplayName } from '@/lib/languageFlags'
import { cn } from '@/lib/utils'

interface LexiconEntry {
  original: string
  replacement: string
  type: 'replacement' | 'phonetic'
}

interface Lexicon {
  [language: string]: LexiconEntry[]
}

interface LexiconEditDialogProps {
  className?: string
}

export function LexiconEditDialog({ className }: LexiconEditDialogProps) {
  const { t, i18n } = useTranslation()
  const { control, setValue, getValues } = useFormContext()

  // Watch form values
  const lexicon = useWatch({ control, name: 'lexicon' }) as Lexicon | undefined
  const defaultLanguage = useWatch({ control, name: 'default_language' }) as string
  const additionalLanguages = useWatch({ control, name: 'additional_languages' }) as string[]
  const voiceId = useWatch({ control, name: 'voice_id' }) as number
  const assistantId = useWatch({ control, name: 'id' }) as number

  const [isOpen, setIsOpen] = useState(false)
  const [localLexicon, setLocalLexicon] = useState<Lexicon>({})
  const [playingKey, setPlayingKey] = useState<string | null>(null)

  // Get all languages (default + additional)
  const allLanguages = useMemo(() => {
    const langs = defaultLanguage ? [defaultLanguage] : []
    if (additionalLanguages?.length) {
      langs.push(...additionalLanguages)
    }
    return langs
  }, [defaultLanguage, additionalLanguages])

  // Initialize local lexicon when dialog opens
  useEffect(() => {
    if (isOpen) {
      setLocalLexicon(lexicon || {})
    }
  }, [isOpen, lexicon])

  // Add entry for a language
  const handleAddEntry = (language: string) => {
    setLocalLexicon((prev) => ({
      ...prev,
      [language]: [...(prev[language] || []), { original: '', replacement: '', type: 'replacement' }],
    }))
  }

  // Remove entry
  const handleRemoveEntry = (language: string, index: number) => {
    setLocalLexicon((prev) => ({
      ...prev,
      [language]: prev[language].filter((_, i) => i !== index),
    }))
  }

  // Update entry
  const handleUpdateEntry = (
    language: string,
    index: number,
    field: keyof LexiconEntry,
    value: string
  ) => {
    setLocalLexicon((prev) => ({
      ...prev,
      [language]: prev[language].map((entry, i) =>
        i === index ? { ...entry, [field]: value } : entry
      ),
    }))
  }

  // Play audio preview
  const handlePlay = async (language: string, index: number) => {
    const entry = localLexicon[language]?.[index]
    if (!entry?.original || !voiceId) return

    const key = `${language}-${index}`
    if (playingKey === key) return

    try {
      setPlayingKey(key)
      await playGreetingAudio({
        text: entry.original,
        language,
        type: entry.type,
        lexicon: localLexicon,
        id: assistantId || 0,
        voice_id: voiceId,
      })
    } catch (error) {
      console.error('Failed to play audio:', error)
    } finally {
      setTimeout(() => setPlayingKey(null), 1500)
    }
  }

  // Save and close
  const handleSave = () => {
    // Filter out empty entries and clean up
    const cleanedLexicon: Lexicon = {}
    for (const [lang, entries] of Object.entries(localLexicon)) {
      const validEntries = entries.filter((e) => e.original.trim() && e.replacement.trim())
      if (validEntries.length > 0) {
        cleanedLexicon[lang] = validEntries
      }
    }

    setValue('lexicon', cleanedLexicon, { shouldDirty: true })
    setIsOpen(false)
  }

  // Get total entries count
  const totalEntries = useMemo(() => {
    return Object.values(lexicon || {}).reduce((sum, entries) => sum + entries.length, 0)
  }, [lexicon])

  return (
    <>
      {/* Trigger button */}
      <div className={className}>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('resources.assistants.lexicon.title')}
        </label>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {t('resources.assistants.lexicon.description')}
        </p>
        <Button
          type="button"
          color="secondary"
          className="mt-3"
          onPress={() => setIsOpen(true)}
          iconLeading={PencilLine}
        >
          {t('resources.assistants.lexicon.edit_button')}
          {totalEntries > 0 && (
            <span className="ml-2 rounded-full bg-gray-200 px-2 py-0.5 text-xs dark:bg-gray-700">
              {totalEntries}
            </span>
          )}
        </Button>
      </div>

      {/* Modal */}
      <Modal isOpen={isOpen} onOpenChange={(open) => !open && setIsOpen(false)} size="4xl">
        <ModalContent>
          <ModalHeader onClose={() => setIsOpen(false)}>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
              {t('resources.assistants.lexicon.modal_title')}
            </h2>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t('resources.assistants.lexicon.modal_description')}
            </p>
          </ModalHeader>

          <ModalBody className="max-h-[60vh] overflow-auto">
            {allLanguages.length === 0 ? (
              <p className="py-8 text-center text-gray-500">
                {t('resources.assistants.lexicon.select_language_first')}
              </p>
            ) : (
              <div className="space-y-6">
                {allLanguages.map((language) => (
                  <div key={language} className="rounded-lg border border-gray-200 p-4 dark:border-gray-700">
                    {/* Language header */}
                    <div className="mb-4 flex items-center gap-2">
                      <span className="text-xl">{getLanguageFlag(language)}</span>
                      <h3 className="font-medium text-gray-900 dark:text-gray-100">
                        {getLanguageDisplayName(language, i18n.language) || language}
                      </h3>
                    </div>

                    {/* Entries */}
                    <div className="space-y-3">
                      {(localLexicon[language] || []).map((entry, index) => {
                        const key = `${language}-${index}`
                        const isPlaying = playingKey === key

                        return (
                          <div key={index} className="flex items-center gap-2">
                            {/* Original word */}
                            <Input
                              value={entry.original}
                              onChange={(value) =>
                                handleUpdateEntry(language, index, 'original', value)
                              }
                              placeholder={t('resources.assistants.lexicon.original_word')}
                              className="flex-1"
                            />

                            {/* Type select */}
                            <Select
                              selectedKey={entry.type}
                              onSelectionChange={(key) =>
                                handleUpdateEntry(language, index, 'type', key as string)
                              }
                              options={[
                                { value: 'replacement', label: t('resources.assistants.lexicon.type_replace') },
                                { value: 'phonetic', label: t('resources.assistants.lexicon.type_phonetic') },
                              ]}
                              className="w-32"
                            />

                            {/* Replacement */}
                            <Input
                              value={entry.replacement}
                              onChange={(value) =>
                                handleUpdateEntry(language, index, 'replacement', value)
                              }
                              placeholder={
                                entry.type === 'phonetic' ? 'IPA: /həˈloʊ/' : t('resources.assistants.lexicon.replacement_text')
                              }
                              className="flex-1"
                            />

                            {/* Play button */}
                            <Button
                              type="button"
                              color="tertiary"
                              size="sm"
                              onPress={() => handlePlay(language, index)}
                              isDisabled={!entry.original || !voiceId || isPlaying}
                              className="h-8 w-8 shrink-0 p-0"
                              iconLeading={isPlaying ? Pause : Play}
                            />

                            {/* Remove button */}
                            <Button
                              type="button"
                              color="tertiary"
                              size="sm"
                              onPress={() => handleRemoveEntry(language, index)}
                              className="h-8 w-8 shrink-0 p-0 text-red-500 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
                              iconLeading={Trash2}
                            />
                          </div>
                        )
                      })}

                      {/* Add button */}
                      <Button
                        type="button"
                        color="secondary"
                        size="sm"
                        onPress={() => handleAddEntry(language)}
                        className="w-full"
                        iconLeading={Plus}
                      >
                        {t('resources.assistants.lexicon.add')}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ModalBody>

          <ModalFooter>
            <Button type="button" color="secondary" onPress={() => setIsOpen(false)}>
              {t('resources.generic.cancel')}
            </Button>
            <Button type="button" onPress={handleSave}>
              {t('resources.generic.save')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default LexiconEditDialog
